// src/modules/units/tyres/views/suppliers/suppliers.js
//
// Active-customer supplier management. The backend owns customer scoping; this
// screen sends only the supplier fields from Swagger and never exposes a customer
// selector. Add/edit share one compact editor and deletes confirm inline.

import {
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier
} from '../suppliers.api.js';
import { icon } from '../../shared/tyre-icons.js';
import { esc } from '../../shared/tyre-format.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

function contactLine(supplier) {
  const bits = [supplier.contactName, supplier.contactNumber, supplier.eMailAddress]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
  return bits.length ? bits.join(' · ') : 'No contact details';
}

export function renderSuppliers(viewEl) {
  let disposed = false;
  let loading = false;
  let suppliers = [];
  let editingId = null;
  let deletingId = null;

  const root = el(`
    <div class="tm-suppliers">
      <section class="tm-supplier-editor">
        <div class="tm-supplier-editor-head">
          <div>
            <h2 class="tm-supplier-editor-title" data-editor-title>Add supplier</h2>
            <p class="tm-supplier-editor-sub">Suppliers belong to the active customer.</p>
          </div>
          <span class="tm-supplier-editor-ic">${icon('supplier')}</span>
        </div>

        <div class="tm-supplier-grid">
          <label class="tm-supplier-field">
            <span>Supplier name <span class="tm-req">*</span></span>
            <input class="tm-input" type="text" autocomplete="organization" data-supplier-field="supplierName" />
          </label>
          <label class="tm-supplier-field">
            <span>Contact name</span>
            <input class="tm-input" type="text" autocomplete="name" data-supplier-field="contactName" />
          </label>
          <label class="tm-supplier-field">
            <span>Email address</span>
            <input class="tm-input" type="email" autocomplete="email" data-supplier-field="eMailAddress" />
          </label>
          <label class="tm-supplier-field">
            <span>Contact number</span>
            <input class="tm-input" type="tel" autocomplete="tel" data-supplier-field="contactNumber" />
          </label>
        </div>

        <div class="tm-supplier-msg" data-supplier-msg hidden></div>
        <div class="tm-supplier-editor-actions">
          <button type="button" class="tm-btn-secondary" data-cancel-edit hidden>Cancel edit</button>
          <button type="button" class="tm-btn-primary" data-save-supplier>Add supplier</button>
        </div>
      </section>

      <section class="tm-supplier-list-card">
        <div class="tm-supplier-list-head">
          <h2>Suppliers</h2>
          <span data-supplier-count></span>
        </div>
        <div class="tm-supplier-list" data-supplier-list></div>
      </section>
    </div>
  `);

  const listEl = root.querySelector('[data-supplier-list]');
  const countEl = root.querySelector('[data-supplier-count]');
  const titleEl = root.querySelector('[data-editor-title]');
  const msgEl = root.querySelector('[data-supplier-msg]');
  const saveBtn = root.querySelector('[data-save-supplier]');
  const cancelBtn = root.querySelector('[data-cancel-edit]');
  const fields = Object.fromEntries(
    Array.from(root.querySelectorAll('[data-supplier-field]'))
      .map((input) => [input.dataset.supplierField, input])
  );

  function setMsg(text, kind = '') {
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = `tm-supplier-msg${kind ? ` is-${kind}` : ''}`;
  }

  function setBusy(on) {
    saveBtn.disabled = on;
    cancelBtn.disabled = on;
    Object.values(fields).forEach((input) => { input.disabled = on; });
  }

  function clearEditor({ keepMessage = false } = {}) {
    editingId = null;
    Object.values(fields).forEach((input) => { input.value = ''; });
    titleEl.textContent = 'Add supplier';
    saveBtn.textContent = 'Add supplier';
    cancelBtn.hidden = true;
    if (!keepMessage) setMsg('');
  }

  function startEdit(supplier) {
    editingId = supplier.supplierId;
    deletingId = null;
    fields.supplierName.value = supplier.supplierName || '';
    fields.contactName.value = supplier.contactName || '';
    fields.eMailAddress.value = supplier.eMailAddress || '';
    fields.contactNumber.value = supplier.contactNumber || '';
    titleEl.textContent = 'Edit supplier';
    saveBtn.textContent = 'Save changes';
    cancelBtn.hidden = false;
    setMsg('');
    renderList();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    fields.supplierName.focus();
    fields.supplierName.select();
  }

  function rowMarkup(supplier) {
    const isDeleting = String(deletingId) === String(supplier.supplierId);
    const isEditing = String(editingId) === String(supplier.supplierId);
    const id = esc(supplier.supplierId);

    return `
      <div class="tm-supplier-row${isEditing ? ' is-editing' : ''}" data-supplier-id="${id}">
        <span class="tm-supplier-row-ic">${icon('supplier')}</span>
        <div class="tm-supplier-row-main">
          <span class="tm-supplier-name">${esc(supplier.supplierName || 'Unnamed supplier')}</span>
          <span class="tm-supplier-contact">${esc(contactLine(supplier))}</span>
        </div>
        ${isDeleting ? `
          <div class="tm-supplier-delete-confirm">
            <span>Delete this supplier?</span>
            <button type="button" class="tm-btn-link" data-cancel-delete>Cancel</button>
            <button type="button" class="tm-btn-danger-small" data-confirm-delete>Delete</button>
          </div>` : `
          <div class="tm-supplier-row-actions">
            <button type="button" class="tm-btn-link" data-edit-supplier>Edit</button>
            <button type="button" class="tm-btn-link tm-btn-link-danger" data-delete-supplier>Delete</button>
          </div>`}
      </div>`;
  }

  function renderList() {
    if (loading) {
      countEl.textContent = '';
      listEl.innerHTML = '<div class="tm-supplier-state">Loading suppliers…</div>';
      return;
    }

    countEl.textContent = `${suppliers.length} supplier${suppliers.length === 1 ? '' : 's'}`;

    if (!suppliers.length) {
      listEl.innerHTML = '<div class="tm-supplier-state">No suppliers yet. Add the first supplier above.</div>';
      return;
    }

    listEl.innerHTML = suppliers.map(rowMarkup).join('');
  }

  async function loadList() {
    loading = true;
    renderList();
    const res = await fetchSuppliers({ sort: 'supplierName' });
    if (disposed) return;
    loading = false;

    if (!res.success) {
      suppliers = [];
      countEl.textContent = '';
      listEl.innerHTML = `
        <div class="tm-supplier-state is-error">
          ${esc(res.message || 'Could not load suppliers.')}
          <button type="button" class="tm-btn-link" data-retry-suppliers>Retry</button>
        </div>`;
      return;
    }

    suppliers = Array.isArray(res.data?.results) ? res.data.results : [];
    renderList();
  }

  function payloadFromEditor() {
    return {
      supplierName: fields.supplierName.value.trim(),
      contactName: fields.contactName.value.trim(),
      eMailAddress: fields.eMailAddress.value.trim(),
      contactNumber: fields.contactNumber.value.trim()
    };
  }

  async function saveSupplier() {
    const payload = payloadFromEditor();
    if (!payload.supplierName) {
      setMsg('Enter a supplier name.', 'error');
      fields.supplierName.focus();
      return;
    }
    if (payload.eMailAddress && !fields.eMailAddress.checkValidity()) {
      setMsg('Enter a valid email address.', 'error');
      fields.eMailAddress.focus();
      return;
    }

    setBusy(true);
    setMsg(editingId ? 'Saving changes…' : 'Adding supplier…');

    const wasEdit = !!editingId;
    const res = wasEdit
      ? await updateSupplier(editingId, payload)
      : await createSupplier(payload);

    if (disposed) return;
    setBusy(false);

    if (!res.success) {
      setMsg(res.message || 'Could not save the supplier.', 'error');
      return;
    }

    clearEditor({ keepMessage: true });
    setMsg(wasEdit ? 'Supplier updated.' : 'Supplier added.', 'ok');
    fields.supplierName.focus();
    // List refresh comes from SUPPLIERS_CHANGED emitted by the API layer.
  }

  async function confirmDelete(supplierId) {
    const supplier = suppliers.find((s) => String(s.supplierId) === String(supplierId));
    if (!supplier) return;

    const res = await deleteSupplier(supplierId);
    if (disposed) return;

    if (!res.success) {
      deletingId = null;
      setMsg(res.message || 'Could not delete the supplier.', 'error');
      renderList();
      return;
    }

    if (String(editingId) === String(supplierId)) clearEditor({ keepMessage: true });
    deletingId = null;
    setMsg(`Supplier “${supplier.supplierName}” deleted.`, 'ok');
    // List refresh comes from SUPPLIERS_CHANGED.
  }

  saveBtn.addEventListener('click', saveSupplier);
  cancelBtn.addEventListener('click', () => clearEditor());
  Object.values(fields).forEach((input) => {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); saveSupplier(); }
    });
  });

  listEl.addEventListener('click', (e) => {
    if (e.target.closest('[data-retry-suppliers]')) { loadList(); return; }

    const row = e.target.closest('[data-supplier-id]');
    if (!row) return;
    const id = row.dataset.supplierId;
    const supplier = suppliers.find((s) => String(s.supplierId) === String(id));

    if (e.target.closest('[data-edit-supplier]') && supplier) { startEdit(supplier); return; }
    if (e.target.closest('[data-delete-supplier]')) { deletingId = id; renderList(); return; }
    if (e.target.closest('[data-cancel-delete]')) { deletingId = null; renderList(); return; }
    if (e.target.closest('[data-confirm-delete]')) confirmDelete(id);
  });

  viewEl.replaceChildren(root);
  const offSuppliers = onTyreEvent(TYRE_EVENTS.SUPPLIERS_CHANGED, loadList);
  loadList();
  fields.supplierName.focus();

  return () => {
    disposed = true;
    offSuppliers();
  };
}
